// Pure helpers over GET /admin/fanwave/timeline — everything the timeline editor needs to place
// waves, phases, gaps and conflicts on one axis, kept away from the components so the arithmetic
// can be reasoned about (and tested) on its own.
//
// The response is the object at the TOP LEVEL: there is no `data` wrapper on this endpoint.

import {
  ANCHOR_KIND,
  MATCH_EVENT,
  MATCH_EVENT_LABEL,
  MATCH_EVENT_ORDER,
  offsetMs
} from './anchors';
import { WAVE_STATUS, isTerminalStatus } from './constants';

const MS_PER_MIN = 60 * 1000;

/** Conflict kinds the endpoint reports. `blocked` is new — check every exhaustive switch. */
export const CONFLICT_KIND = {
  /** Spans intersect. Create/update refuse this, so it appears mainly after a fixture moved. */
  OVERLAP: 'overlap',
  /** A wave sitting inside a wave on a DIFFERENT fixture. It will never open. */
  BLOCKED: 'blocked'
};

/**
 * The `schedule` values a wave row may carry. `mixed` is NEW — one edge on the match clock and
 * the other on the wall clock (e.g. startMinute + endsAt) — and any switch that predates it falls
 * through, which is why this list lives next to a total label map rather than in each caller.
 */
export const SCHEDULE_KIND = {
  DEFAULT: 'default',
  MINUTES: 'minutes',
  WINDOW: 'window',
  MIXED: 'mixed'
};

export const SCHEDULE_KIND_LABEL = {
  [SCHEDULE_KIND.DEFAULT]: 'Default (half-time → full-time + window)',
  [SCHEDULE_KIND.MINUTES]: 'Match minutes',
  [SCHEDULE_KIND.WINDOW]: 'Clock window',
  [SCHEDULE_KIND.MIXED]: 'Mixed — one edge on the match clock, one on the wall clock'
};

const toDate = (value) => {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * The raw response as Dates, with the derived indexes the components read.
 *
 * Waves that cannot be placed (either edge unparseable) are dropped rather than drawn at the
 * epoch — the endpoint already refuses to place a fixture with no kickoff, so anything arriving
 * here without both edges is a shape the axis cannot honestly represent.
 */
export const normalizeTimeline = (res) => {
  if (!res || typeof res !== 'object') return null;

  const waves = (res.waves || [])
    .map((wave) => {
      const opensAt = toDate(wave.opensAt);
      const closesAt = toDate(wave.closesAt);
      if (!opensAt || !closesAt) return null;
      return { ...wave, opensAt, closesAt, pending: !!wave.pending };
    })
    .filter(Boolean)
    .sort((a, b) => a.opensAt - b.opensAt);

  const phases = orderedPhases(res.phases);

  return {
    teamId: res.teamId || null,
    gameId: res.gameId || null,
    postFtMinutes: Number(res.postFtMinutes) || 0,
    phases,
    phaseByEvent: new Map(phases.map((p) => [p.event, p])),
    waves,
    waveById: new Map(waves.map((w) => [w.waveId, w])),
    gaps: (res.gaps || [])
      .map((gap) => {
        const from = toDate(gap.from);
        const to = toDate(gap.to);
        if (!from || !to) return null;
        return { from, to, minutes: Number(gap.minutes) || Math.round((to - from) / MS_PER_MIN) };
      })
      .filter(Boolean),
    conflicts: res.conflicts || [],
    conflictsByWave: indexConflicts(res.conflicts)
  };
};

/**
 * Phases sorted by resolved time, with the canonical match order as the tie-break.
 *
 * The tie-break is load-bearing, not defensive: before kickoff the backend projects extra-time
 * and full-time to the SAME instant and penalties to 30 minutes after both, so a plain sort by
 * time puts full-time in the middle of the strip. Sorting by time first still respects a real
 * observed order once the match has actually played out.
 */
export const orderedPhases = (phases) =>
  (phases || [])
    .map((phase) => {
      const at = toDate(phase.at);
      return at ? { event: phase.event, at, observed: !!phase.observed } : null;
    })
    .filter(Boolean)
    .sort((a, b) => {
      const diff = a.at - b.at;
      if (diff !== 0) return diff;
      return MATCH_EVENT_ORDER.indexOf(a.event) - MATCH_EVENT_ORDER.indexOf(b.event);
    });

/** waveId → its conflicts. A wave can be in more than one, so the values are arrays. */
export const indexConflicts = (conflicts) => {
  const map = new Map();
  (conflicts || []).forEach((conflict) => {
    if (!conflict?.waveId) return;
    const list = map.get(conflict.waveId) || [];
    list.push(conflict);
    map.set(conflict.waveId, list);
  });
  return map;
};

/**
 * The DISTINCT boundaries on the axis — one per instant, however many events land on it.
 *
 * Collapsing is load-bearing before kickoff, where extra-time and full-time project to the same
 * instant: two boundaries at one position would draw two overlapping bands and two markers on top
 * of each other. The representative is the LAST event in canonical order (the most advanced phase
 * the instant stands for), and the rest are carried on `coincident` so the marker can admit that
 * several phases project here rather than silently dropping them.
 */
export const phaseBoundaries = (phases) => {
  const list = orderedPhases(phases);
  const groups = [];
  list.forEach((phase) => {
    const last = groups[groups.length - 1];
    if (last && last.at.getTime() === phase.at.getTime()) {
      last.events.push(phase);
      return;
    }
    groups.push({ at: phase.at, events: [phase] });
  });

  return groups.map((group) => {
    const ordered = [...group.events].sort(
      (a, b) => MATCH_EVENT_ORDER.indexOf(a.event) - MATCH_EVENT_ORDER.indexOf(b.event)
    );
    const lead = ordered[ordered.length - 1];
    return {
      at: group.at,
      event: lead.event,
      // Observed only if the phase the boundary is named for was actually seen.
      observed: lead.observed,
      coincident: ordered.slice(0, -1).map((p) => p.event)
    };
  });
};

/**
 * The strip's bands — one per stretch of match, labelled by the phase that OPENS it.
 *
 * Built from `phaseBoundaries` rather than a fixed five-band template, which is what keeps it
 * honest before kickoff: a template would draw a zero-width "Extra time" band and start
 * "After full-time" before penalties, both of which the data does not support.
 *
 * `observed` on a band is its OPENING boundary's flag — the strip draws that edge solid or dashed.
 */
export const phaseBands = (phases, window) => {
  const bounds = phaseBoundaries(phases);
  if (!bounds.length || !window?.from || !window?.to) return [];

  const bands = [];
  const first = bounds[0];
  if (window.from < first.at) {
    bands.push({
      key: 'pre_match',
      label: first.event === MATCH_EVENT.KICKOFF ? 'Pre-match' : 'Before the match',
      from: window.from,
      to: first.at,
      observed: true,
      event: null
    });
  }

  bounds.forEach((boundary, i) => {
    const to = i + 1 < bounds.length ? bounds[i + 1].at : window.to;
    if (to <= boundary.at) return;
    bands.push({
      key: boundary.event,
      label: bandLabel(boundary.event),
      from: boundary.at,
      to,
      observed: boundary.observed,
      event: boundary.event
    });
  });

  return bands;
};

const BAND_LABEL = {
  [MATCH_EVENT.KICKOFF]: 'First half',
  [MATCH_EVENT.HALF_TIME]: 'Half-time',
  [MATCH_EVENT.SECOND_HALF]: 'Second half',
  [MATCH_EVENT.EXTRA_TIME]: 'Extra time',
  [MATCH_EVENT.PENALTIES]: 'Penalties',
  [MATCH_EVENT.FULL_TIME]: 'After full-time'
};

const bandLabel = (event) => BAND_LABEL[event] || MATCH_EVENT_LABEL[event] || event;

/**
 * The axis the track is drawn on: everything that has to fit, plus a margin so a wave opening on
 * the first boundary is not flush against the edge.
 *
 * Deliberately spans the union of the PHASES and the WAVES. A wave anchored to a distant
 * date-time is the case the timeline exists to make visible, so the axis stretches to hold it
 * rather than clipping it out of sight.
 */
export const trackWindow = ({
  phases,
  waves,
  now,
  // Moments the axis must cover that the timeline response does not carry. Today that is the
  // fixture's kick-off: the endpoint resolves phases only for the fixtures its WAVES run on, so a
  // fixture with no waves yet comes back with `phases: []` and nothing to build an axis from —
  // which is exactly when an admin needs the track, to place the first wave on it. The CMS
  // already knows the kick-off from the fixture list, so it supplies it here.
  extraStamps = [],
  padRatio = 0.04,
  minSpanMin = 30
}) => {
  const stamps = [];
  orderedPhases(phases).forEach((p) => stamps.push(p.at.getTime()));
  (waves || []).forEach((w) => {
    stamps.push(w.opensAt.getTime());
    stamps.push(w.closesAt.getTime());
  });
  extraStamps.forEach((value) => {
    const date = toDate(value);
    if (date) stamps.push(date.getTime());
  });
  if (!stamps.length) return null;

  let from = Math.min(...stamps);
  let to = Math.max(...stamps);

  // A single instant (one zero-length wave, or one phase) has no span to divide by — give it a
  // readable minimum rather than letting `positionIn` divide by zero.
  if (to - from < minSpanMin * MS_PER_MIN) {
    const centre = (from + to) / 2;
    const half = (minSpanMin * MS_PER_MIN) / 2;
    from = centre - half;
    to = centre + half;
  }

  const pad = (to - from) * padRatio;
  const window = { from: new Date(from - pad), to: new Date(to + pad) };
  const nowDate = toDate(now);
  window.now = nowDate && nowDate >= window.from && nowDate <= window.to ? nowDate : null;
  return window;
};

/** Where a moment sits on the axis, as a 0–100 percentage. Clamped, so nothing renders off-track. */
export const positionIn = (window, at) => {
  const date = toDate(at);
  if (!window?.from || !window?.to || !date) return null;
  const span = window.to.getTime() - window.from.getTime();
  if (span <= 0) return 0;
  const pct = ((date.getTime() - window.from.getTime()) / span) * 100;
  return Math.min(100, Math.max(0, pct));
};

/** The inverse — a 0–100 position back to a moment, for drag handles. */
export const timeAt = (window, pct) => {
  if (!window?.from || !window?.to) return null;
  const span = window.to.getTime() - window.from.getTime();
  const clamped = Math.min(100, Math.max(0, Number(pct) || 0));
  return new Date(window.from.getTime() + (span * clamped) / 100);
};

/**
 * The ONE session track: every wave as a consecutive segment, with the reported gaps between
 * them as empty slots.
 *
 * Waves are laid out in a single row and never stacked, which is the point — only one wave can
 * hold a team's session at a time, so overlap is not a state to render but a state to make
 * undrawable. A wave the endpoint reports as conflicting is still drawn in sequence and flagged,
 * because it exists in the data; what the editor refuses to do is let the admin CREATE one.
 *
 * Returns segments in axis order: `{ type: 'wave' | 'gap', ... }`.
 */
export const buildTrack = (timeline) => {
  if (!timeline) return [];
  const segments = timeline.waves.map((wave) => ({
    type: 'wave',
    key: wave.waveId,
    from: wave.opensAt,
    to: wave.closesAt,
    wave,
    conflicts: timeline.conflictsByWave.get(wave.waveId) || []
  }));

  timeline.gaps.forEach((gap, i) => {
    segments.push({
      type: 'gap',
      key: `gap-${i}`,
      from: gap.from,
      to: gap.to,
      minutes: gap.minutes
    });
  });

  return segments.sort((a, b) => a.from - b.from || a.to - b.to);
};

/** Segment fill by lifecycle status — completed muted, active filled, scheduled dashed. */
export const SEGMENT_FILL = {
  [WAVE_STATUS.SCHEDULED]: 'dashed',
  [WAVE_STATUS.ACTIVE]: 'filled',
  [WAVE_STATUS.COMPLETED]: 'muted',
  [WAVE_STATUS.CANCELLED]: 'muted',
  [WAVE_STATUS.EXPIRED]: 'muted'
};

export const segmentFill = (wave) => SEGMENT_FILL[wave?.status] || 'dashed';

/**
 * Snapping — what a dragged handle BECOMES when it is dropped at a moment.
 *
 * This is the mechanism that makes presets A–D reachable without a mode switch: drop a handle on
 * the half-time boundary and you have written `half_time +0`, which IS preset A's open. Three
 * rules, coarsest last:
 *
 *   near a phase boundary  → that EVENT, offset 0 (the exact thing, and it moves with the match)
 *   inside the match       → a MATCH MINUTE (the clock is running; no event is close enough)
 *   outside the match      → a DATE-TIME (nothing on the match clock to hang it on)
 *
 * `toleranceMin` is in wall-clock minutes and should come from the pixel width of the track, so
 * the snap zone is a constant number of PIXELS rather than a constant number of minutes — a
 * three-day calendar axis would otherwise snap everything to the nearest phase.
 */
export const snapToAnchor = (at, { phases, toleranceMin = 3, toLocal } = {}) => {
  const date = toDate(at);
  if (!date) return null;
  const list = orderedPhases(phases);

  let nearest = null;
  let nearestGap = Infinity;
  list.forEach((phase) => {
    const gap = Math.abs(phase.at.getTime() - date.getTime());
    if (gap < nearestGap) {
      nearest = phase;
      nearestGap = gap;
    }
  });

  if (nearest && nearestGap <= toleranceMin * MS_PER_MIN) {
    return { kind: ANCHOR_KIND.MATCH_EVENT, event: nearest.event, offsetMin: 0 };
  }

  const minute = wallClockToMatchMinute(date, list);
  if (minute != null) return { kind: ANCHOR_KIND.MATCH_MINUTE, minute };

  return { kind: ANCHOR_KIND.ABSOLUTE, at: toLocal ? toLocal(date) : date.toISOString() };
};

/**
 * A wall-clock moment back to the match minute it falls on, or null when it falls outside the
 * match entirely (before kick-off, or after the final whistle).
 *
 * The inverse of the backend's `minuteToWallClock`, and it makes the same choice: once the
 * second-half restart has been OBSERVED, minutes past the interval are measured from it, so a
 * long or short break is accounted for exactly instead of assumed at 15 minutes.
 */
export const wallClockToMatchMinute = (at, phases) => {
  const date = toDate(at);
  const list = orderedPhases(phases);
  const byEvent = new Map(list.map((p) => [p.event, p]));
  const kickoff = byEvent.get(MATCH_EVENT.KICKOFF);
  const fullTime = byEvent.get(MATCH_EVENT.FULL_TIME);
  if (!date || !kickoff || date < kickoff.at) return null;
  if (fullTime && date > fullTime.at) return null;

  const secondHalf = byEvent.get(MATCH_EVENT.SECOND_HALF);
  const raw =
    secondHalf && date >= secondHalf.at
      ? 45 + (date.getTime() - secondHalf.at.getTime()) / MS_PER_MIN
      : (date.getTime() - kickoff.at.getTime()) / MS_PER_MIN;

  const minute = Math.round(raw);
  if (minute < 0 || minute > 150) return null;
  return minute;
};

/**
 * Resolves an anchor pair onto the wall clock the way the backend does, so the editor can order
 * two edges on DIFFERENT clocks before submitting.
 *
 * Only as good as the phases it is given — a `duration` close and a `match_minute` edge are both
 * projections — which is exactly why this is used for ORDERING and never presented as a time the
 * wave will open. `observed` is carried through so a caller can say "~" where it matters.
 */
export const resolveAnchorLocally = (anchor, { phaseByEvent, postFtMinutes, openResolved }) => {
  if (!anchor?.kind) return null;

  if (anchor.kind === ANCHOR_KIND.ABSOLUTE) {
    const at = toDate(anchor.at);
    return at ? { at, observed: true } : null;
  }

  if (anchor.kind === ANCHOR_KIND.DURATION) {
    if (!openResolved || anchor.durationMin == null) return null;
    const timed = {
      at: new Date(openResolved.at.getTime() + offsetMs(anchor.durationMin)),
      // A duration is only as certain as the open it is measured from.
      observed: openResolved.observed
    };
    if (!anchor.untilEvent) return timed;
    // Capped: the event closes it instead when it falls sooner — mirrors the API's resolver.
    const cap = resolveAnchorLocally(
      { kind: ANCHOR_KIND.MATCH_EVENT, event: anchor.untilEvent },
      { phaseByEvent, postFtMinutes }
    );
    return cap && cap.at < timed.at ? cap : timed;
  }

  const phases = phaseByEvent instanceof Map ? phaseByEvent : new Map();

  if (anchor.kind === ANCHOR_KIND.MATCH_MINUTE) {
    const kickoff = phases.get(MATCH_EVENT.KICKOFF);
    if (!kickoff || anchor.minute == null) return null;
    const secondHalf = phases.get(MATCH_EVENT.SECOND_HALF);
    const minute = Number(anchor.minute);
    const at =
      minute > 45 && secondHalf
        ? new Date(secondHalf.at.getTime() + (minute - 45) * MS_PER_MIN)
        : new Date(kickoff.at.getTime() + (minute + (minute > 45 ? 15 : 0)) * MS_PER_MIN);
    // Never observed: the wall-clock time a minute maps to is reconstructed, not recorded.
    return { at, observed: false };
  }

  const phase = phases.get(anchor.event);
  if (!phase) return null;
  return {
    at: new Date(phase.at.getTime() + offsetMs(anchor.offsetMin)),
    observed: phase.observed
  };
};

/**
 * The ordering check that needs the fixture — a close resolving at or before its open across two
 * different clocks (half-time → a date-time that falls during the first half).
 *
 * Returns an error string or null. Silent when either edge cannot be resolved locally: an
 * unplaceable edge is not a proven mistake, and blocking a save on a projection the CMS could not
 * compute would be worse than letting the API have the last word.
 */
export const orderingError = (open, close, { phaseByEvent, postFtMinutes }) => {
  const openResolved = resolveAnchorLocally(open, { phaseByEvent, postFtMinutes });
  if (!openResolved) return null;
  const closeResolved = resolveAnchorLocally(close, {
    phaseByEvent,
    postFtMinutes,
    openResolved
  });
  if (!closeResolved) return null;
  if (closeResolved.at.getTime() > openResolved.at.getTime()) return null;
  return 'On this fixture the closing edge lands at or before the opening edge — the wave would never run';
};

/**
 * Whether a proposed span collides with a wave already on the track — the client-side twin of the
 * API's overlap refusal, so the editor can rule a drop out instead of letting the admin discover
 * it through a 400.
 *
 * Half-open, exactly as the API compares: touching at a boundary is fine (one closes 16:30, the
 * next opens 16:30), which is what makes "append where the previous one closed" a legal default.
 * Terminal waves are ignored — they have already run and take nothing away.
 */
export const findTrackConflict = (span, waves, { ignoreWaveId } = {}) => {
  if (!span?.from || !span?.to) return null;
  return (
    (waves || []).find(
      (wave) =>
        wave.waveId !== ignoreWaveId &&
        !isTerminalStatus(wave.status) &&
        span.from < wave.closesAt &&
        span.to > wave.opensAt
    ) || null
  );
};

/**
 * Where an added wave OPENS by default: exactly where the last still-runnable wave closes.
 *
 * Built deliberately rather than defaulting to "now". Appending on the previous close makes the
 * common case one click and guarantees the result is neither an overlap (it starts at the
 * boundary, and the comparison is half-open) nor a gap (there is nothing between them). Null when
 * the track is empty — there is no predecessor to hand over from, and the caller falls back to a
 * preset instead of inventing a time.
 */
export const appendOpenAt = (timeline) => {
  const runnable = (timeline?.waves || []).filter((w) => !isTerminalStatus(w.status));
  if (!runnable.length) return null;
  return runnable.reduce(
    (latest, w) => (w.closesAt > latest ? w.closesAt : latest),
    runnable[0].closesAt
  );
};

/**
 * The stretches of wall clock this team has NOT given away — the windows another wave can be
 * placed in, earliest first.
 *
 * The timeline twin of `freeGaps` in constants.js, and the difference matters: that one projects
 * legacy waves off kick-off estimates, while this reads the RESOLVED spans the endpoint already
 * computed, so the bounds agree with the segments actually drawn on the track.
 *
 * A null `from` means "anything before this" and a null `to` means open-ended. Terminal waves are
 * skipped — they have run, and take nothing away. Merged on a running maximum close, because a
 * wave sitting entirely inside a longer one leaves no window between them.
 */
export const freeSlots = (timeline, { ignoreWaveId } = {}) => {
  const spans = (timeline?.waves || [])
    // A wave being EDITED must not block itself: its current span is the one it is moving off.
    .filter((wave) => wave.waveId !== ignoreWaveId && !isTerminalStatus(wave.status))
    .map((wave) => ({ from: wave.opensAt, to: wave.closesAt }))
    .sort((a, b) => a.from - b.from);

  const slots = [];
  let coveredTo = null;
  spans.forEach((span) => {
    if (!coveredTo || span.from > coveredTo) {
      slots.push({ from: coveredTo, to: span.from });
    }
    if (!coveredTo || span.to > coveredTo) coveredTo = span.to;
  });
  // What is left after the last wave closes — where an added wave usually goes.
  slots.push({ from: coveredTo, to: null });
  return slots;
};

/**
 * Which free slot a moment falls in — the one a wave opening then would have to fit inside.
 *
 * Falls back to the LAST slot when the moment lands in none: either nothing is chosen yet, or the
 * time is one already given away. Both are the same case for the editor — the admin is adding a
 * wave after the ones already there, and that is the window to offer.
 */
export const slotAt = (slots, at) => {
  const list = slots || [];
  if (!list.length) return null;
  const date = toDate(at);
  const holding = date
    ? list.find((slot) => (!slot.from || date >= slot.from) && (!slot.to || date < slot.to))
    : null;
  return holding || list[list.length - 1];
};

/**
 * The `min`/`max` the two date-time fields carry, so the picker itself greys out the times this
 * team has already given away rather than letting the admin choose one and be refused.
 *
 * `openAt` is where the wave being placed opens; it selects WHICH free slot the fields are held
 * inside, which is what keeps a hole BEFORE the existing waves reachable instead of pinning every
 * new wave after the last one. The close carries two further rules the editor validates anyway —
 * after its own open, and in the future — so that they are visible before submitting.
 *
 * `toLocal` formats for a `datetime-local` input. The open's minimum is rounded UP and the
 * maximum DOWN to the whole minute the field can hold: rounding the wrong way would offer a
 * boundary minute that resolves back inside the neighbouring wave.
 */
export const slotBounds = (timeline, openAt, { now = new Date(), toLocal, ignoreWaveId } = {}) => {
  const slot = slotAt(freeSlots(timeline, { ignoreWaveId }), openAt);
  if (!slot) return null;
  const fmt = (date) => (date && toLocal ? toLocal(date) : '');
  const from = slot.from ? ceilToMinute(slot.from) : null;
  const to = slot.to ? floorToMinute(slot.to) : null;

  // A hole narrower than the minute the field can express. Rounding the two bounds inwards has
  // crossed them over, and emitting min > max would be a field that rejects every value with no
  // explanation. Report it as unusable instead, so the caller can say why.
  if (from && to && from > to) {
    return { slot, openMin: '', openMax: '', closeMin: '', closeMax: '', tooNarrow: true };
  }

  const open = toDate(openAt);
  // As text, `datetime-local` values sort chronologically, so the latest lower bound is just the
  // last one.
  const closeMin =
    [fmt(from), fmt(open), fmt(ceilToMinute(now))].filter(Boolean).sort().pop() || '';

  return {
    slot,
    openMin: fmt(from),
    openMax: fmt(to),
    closeMin,
    closeMax: fmt(to),
    tooNarrow: false
  };
};

/**
 * A moment rounded UP to the next whole minute.
 *
 * This exists because of a precision mismatch that quietly breaks the append default. A wave's
 * resolved close carries seconds (it is kick-off plus an offset, and kick-off is a real
 * timestamp), but a `datetime-local` field is minute-precision and TRUNCATES them. Seeding the new
 * wave's open with the raw handover therefore lands it a few seconds BEFORE the predecessor closes
 * — an overlap, in the one flow whose whole point is that it cannot produce one.
 *
 * Rounding up gives away at most 59 seconds and keeps the guarantee: the seeded open is at or
 * after the previous close, and the overlap comparison is half-open, so touching is legal.
 */
export const ceilToMinute = (at) => {
  const date = toDate(at);
  if (!date) return null;
  const ms = date.getTime();
  const rounded = Math.ceil(ms / MS_PER_MIN) * MS_PER_MIN;
  return new Date(rounded);
};

/** The counterpart of `ceilToMinute`, for an upper bound that must not exceed the real one. */
export const floorToMinute = (at) => {
  const date = toDate(at);
  if (!date) return null;
  return new Date(Math.floor(date.getTime() / MS_PER_MIN) * MS_PER_MIN);
};

/**
 * The copy for a conflict, and how loudly to say it.
 *
 * `blocked` is the one nothing else in the system reports: the wave reads as `scheduled` right up
 * until it flips to `expired`, having never run, and the create-time check is scoped to a single
 * fixture so it cannot see this at all. That makes the timeline the only place it surfaces, which
 * is why it gets the danger treatment and names both the blocker and when the session frees.
 */
export const describeConflict = (conflict, timeline, fmtDateTime) => {
  const other = timeline?.waveById?.get(conflict.otherWaveId) || null;
  const otherName = other
    ? other.gameId && other.gameId !== timeline.gameId
      ? 'a wave on another fixture'
      : 'another wave on this fixture'
    : 'another wave';
  const until = other?.closesAt ? fmtDateTime(other.closesAt) : null;

  if (conflict.kind === CONFLICT_KIND.BLOCKED) {
    return {
      tone: 'danger',
      title: 'This wave will never open',
      detail: until
        ? `${otherName} holds the team's session until ${until}. Only one wave runs at a time, so this one stays scheduled and then expires without ever opening.`
        : `${otherName} holds the team's session for this whole window. Only one wave runs at a time, so this one stays scheduled and then expires without ever opening.`
    };
  }

  if (conflict.kind === CONFLICT_KIND.OVERLAP) {
    return {
      tone: 'warning',
      title: 'Overlaps another wave',
      detail: until
        ? `Its window intersects ${otherName}, which runs until ${until}. Waves are refused at creation when they overlap, so this usually means the fixture moved underneath it.`
        : `Its window intersects ${otherName}. Waves are refused at creation when they overlap, so this usually means the fixture moved underneath it.`
    };
  }

  // A kind this build has not seen. Say that, rather than falling through and showing nothing —
  // `conflicts[].kind` has grown once already (`blocked`), and it can grow again.
  return {
    tone: 'warning',
    title: 'Scheduling conflict',
    detail: `The API reports a "${conflict.kind}" conflict with ${otherName}. This build of the CMS does not have copy for it yet.`
  };
};

/** Is this wave the one currently holding the session? Drives the playhead and the live chip. */
export const liveWaveOf = (timeline) =>
  (timeline?.waves || []).find((w) => w.status === WAVE_STATUS.ACTIVE) || null;
